import { LitElement, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { uploadMedia, addMedia } from '../client/ApiClient';

@customElement('upload-media-form')
export class UploadMediaForm extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: String }) slug = '';
  @state() primaryFiles: File[] = [];
  @state() secondaryFiles: File[] = [];
  @state() working = false;

  handleFileInput(e: Event, isPrimary: boolean) {
    const input = e.target as HTMLInputElement;
    if (!input.files?.length) return;

    const files = Array.from(input.files).filter(f => f.type.startsWith('image/') || f.type === 'video/mp4');
    if (files.length === 0) return;

    if (isPrimary) {
      this.primaryFiles = [...this.primaryFiles, ...files];
    } else {
      this.secondaryFiles = [...this.secondaryFiles, ...files];
    }
  }

  renderPreview(files: File[]) {
    return html`
      <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        ${files.map(f => {
          const url = URL.createObjectURL(f);
          return html`
            <div class="relative group">
              <img src=${url} class="rounded object-cover max-h-40 w-full" />
            </div>
          `;
        })}
      </div>
    `;
  }

  async handleSubmit() {
    if (!this.slug || this.working) return;
    this.working = true;

    try {
      for (const file of this.primaryFiles) {
        const { media_id } = await uploadMedia(file);
        await addMedia(this.slug, media_id, true);
      }
      for (const file of this.secondaryFiles) {
        const { media_id } = await uploadMedia(file);
        await addMedia(this.slug, media_id, false);
      }

      this.primaryFiles = [];
      this.secondaryFiles = [];
      this.dispatchEvent(new CustomEvent('media-added', { bubbles: true }));
    } catch (err) {
      alert(`Upload failed: ${err.message}`);
    } finally {
      this.working = false;
    }
  }

  render() {
    const hasFiles = this.primaryFiles.length > 0 || this.secondaryFiles.length > 0;

    return html`
      <div class="card bg-base-200 p-6 mb-6 shadow space-y-6">
        <h3 class="text-xl font-bold">Upload Media</h3>

        <!-- Primary Upload Section -->
        <div>
          <label class="font-semibold mb-1 block">Primary Media (Directory or Multiple Files)</label>
          <p class="text-sm text-base-content/70 mb-2">
            Positive conditioning examples (e.g. images of the person you're training).
          </p>
          <input
            type="file"
            accept="image/*,video/mp4"
            class="file-input file-input-bordered w-full max-w-xs"
            webkitdirectory
            directory
            multiple
            @change=${(e: Event) => this.handleFileInput(e, true)}
          />
          ${this.renderPreview(this.primaryFiles)}
        </div>

        <!-- Secondary Upload Section -->
        <div>
          <label class="font-semibold mb-1 block">Secondary Media (Optional)</label>
          <p class="text-sm text-base-content/70 mb-2">
            Negative examples to help disambiguate the subject.
          </p>
          <input
            type="file"
            accept="image/*,video/mp4"
            class="file-input file-input-bordered w-full max-w-xs"
            webkitdirectory
            directory
            multiple
            @change=${(e: Event) => this.handleFileInput(e, false)}
          />
          ${this.renderPreview(this.secondaryFiles)}
        </div>

        <!-- Submit Button -->
        <div class="text-center pt-4">
          <button
            class="btn btn-primary"
            ?disabled=${this.working || !hasFiles}
            @click=${this.handleSubmit}>
            ${this.working ? 'Uploading…' : '📤 Upload Selected Media'}
          </button>
        </div>
      </div>
    `;
  }
}

